import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, FileText } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  children?: Category[];
}

interface UploadFile {
  id: number;
  file: File;
  name: string;
  size: string;
  type: string;
  category: string;
  permissions: string[];
  description: string;
  tags: string[];
  status: string;
}

interface UploadModalProps {
  selectedCategory: string;
  categories: Category[];
  onClose: () => void;
}

const UploadModal = ({ selectedCategory, categories, onClose }: UploadModalProps) => {
  const { t } = useTranslation();
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(selectedCategory);
  const [permissions, setPermissions] = useState(['employee', 'manager', 'admin']);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Flatten categories for dropdown
  const flatCategories = categories?.reduce((acc: Category[], category) => {
    if (category?.id !== 'all') {
      acc?.push(category);
      if (category?.children) {
        category?.children?.forEach(child => {
          acc?.push({ ...child, name: `${category?.name} / ${child?.name}` });
        });
      }
    }
    return acc;
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    const files = Array.from(e?.dataTransfer?.files);
    handleFiles(files);
  }, [selectedCategoryId, permissions]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e?.target?.files || []);
    handleFiles(files);
  }, [selectedCategoryId, permissions]);

  const handleFiles = (files: File[]) => {
    const validFiles = files?.filter(file => {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'image/jpeg',
        'image/png'
      ];
      return validTypes?.includes(file?.type) && file?.size <= 10 * 1024 * 1024; // 10MB limit
    });

    const processedFiles = validFiles?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file?.name,
      size: formatFileSize(file?.size),
      type: getFileType(file?.type),
      category: selectedCategoryId,
      permissions: [...permissions],
      description: '',
      tags: [],
      status: 'pending'
    }));

    setUploadFiles(prev => [...prev, ...processedFiles]);
  };

  const getFileType = (mimeType: string) => {
    const typeMap: Record<string, string> = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'image/jpeg': 'jpg',
      'image/png': 'png'
    };
    return typeMap[mimeType] || 'file';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = (fileId: number) => {
    setUploadFiles(prev => prev?.filter(file => file?.id !== fileId));
  };

  const updateFile = (fileId: number, updates: Partial<UploadFile>) => {
    setUploadFiles(prev =>
      prev?.map(file =>
        file?.id === fileId ? { ...file, ...updates } : file
      )
    );
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setUploadProgress(0);

    // Mock upload process
    for (let i = 0; i <= 100; i += 10) {
      setTimeout(() => {
        setUploadProgress(i);
        if (i === 100) {
          setTimeout(() => {
            setIsUploading(false);
            onClose();
            // Show success message
          }, 500);
        }
      }, i * 50);
    }
  };

  const getFileIcon = (type: string) => {
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-gray-900">{t('Upload Documents')}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">

          {!isUploading ? (
            <>
              {/* Upload Area */}
              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-primary bg-primary/5' : 'border-border hover:border-border'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.png"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />

                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('Drop files here or click to browse')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('Support for PDF, Word, Excel, PowerPoint, and image files up to 10MB')}
                </p>
              </div>

              {/* Upload Settings */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Default Category')}
                  </label>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e?.target?.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    {flatCategories?.map(category => (
                      <option key={category?.id} value={category?.id}>
                        {category?.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Default Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Default Permissions')}
                  </label>
                  <div className="space-y-2">
                    {[
                      { id: 'employee', label: t('Employee Access') },
                      { id: 'manager', label: t('Manager Access') },
                      { id: 'admin', label: t('Admin Only') }
                    ]?.map(option => (
                      <label key={option?.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={permissions?.includes(option?.id)}
                          onChange={(e) => {
                            if (e?.target?.checked) {
                              setPermissions(prev => [...prev, option?.id]);
                            } else {
                              setPermissions(prev => prev?.filter(p => p !== option?.id));
                            }
                          }}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-primary/20"
                        />
                        <span className="ml-2 text-sm text-gray-700">{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Files List */}
              {uploadFiles?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {t('Files to Upload')} ({uploadFiles?.length})
                  </h3>

                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {uploadFiles?.map((file) => (
                      <div key={file?.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                        {getFileIcon(file?.type)}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file?.name}
                            </p>
                            <button
                              onClick={() => removeFile(file?.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">{file?.size}</p>
                        </div>

                        <div className="flex items-center space-x-2">
                          <select
                            value={file?.category}
                            onChange={(e) => updateFile(file?.id, { category: e?.target?.value })}
                            className="text-xs border border-border rounded px-2 py-1"
                          >
                            {flatCategories?.map(category => (
                              <option key={category?.id} value={category?.id}>
                                {category?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploadFiles?.length === 0}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('Upload')} {uploadFiles?.length > 0 && `${uploadFiles?.length} ${t('Files')}`}
                </button>
              </div>
            </>
          ) : (
            /* Upload Progress */
            <div className="text-center py-12">
              <Upload className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('Uploading Documents...')}
              </h3>
              <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">
                {uploadProgress}% {t('complete')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
